import { Directive, AfterViewInit, ElementRef, Input } from '@angular/core';

/**
 * This directive is used to apply a max length to an <input> element used for numeric entry.
 * This is required for the <input type="number" /> combination because the browser will not enforce max lengths for number fields.
 */
@Directive({
	selector: '[ccNumericInputMaxLength]'
})
export class NumericInputMaxLengthDirective implements AfterViewInit {

// #region [ Fields ]

	/** The DOM element this directive is tied to. */
	private _Element: ElementRef;

	/** The maximum length to allow. */
	@Input() public maxlength: number;

	/** The maximum numeric value to allow. */
	@Input() public max: number;

	/** The minimum numeric value to allow. */
	@Input() public min: number;

// #region [ Constructor ]

	constructor(Element: ElementRef) {
		this._Element = Element;
	}

// #region [ Implemented Methods ]

	/**
	 * After view initilized event.
	 */
	public ngAfterViewInit(): void {
		let objElement: HTMLInputElement;
		let objKeyDownFunc, objChangeFunc: Function;

		objElement = this._Element.nativeElement;

		// This directive is only meant for numeric entry.
		if (objElement.type == "number" && this.min != null && this.max != null && this.maxlength != null) {
			objKeyDownFunc = this.OnElementKeyDown.bind(this);
			objChangeFunc = this.OnElementChange.bind(this);

			objElement.addEventListener("keydown", function (objEvent) { objKeyDownFunc(objEvent) });
			objElement.addEventListener("change", function () { objChangeFunc() });
		}
	}

// #region [ Event Handlers ]

	/**
	 * Event handler for when the field the directive is tied to changes.
	 */
	public OnElementChange() {
		let objElement: HTMLInputElement;
		let dblValue: number;

		objElement = this._Element.nativeElement;
		dblValue = parseFloat(objElement.value);

		if (!isNaN(dblValue)) {
			if (dblValue < this.min)
				objElement.value = this.min.toString();
			else if (dblValue > this.max)
				objElement.value = this.max.toString();
		}
	}

	/**
	 * Event handler for when a key is pressed while the field the directive is tied to has focus.
	 * 
	 * @param Event The event for the key down.
	 */
	public OnElementKeyDown(Event: KeyboardEvent) {
		let objElement: HTMLInputElement;
		let strValue: string;
		let dblValue: number;

		objElement = this._Element.nativeElement;
		strValue = objElement.value;
		dblValue = parseFloat(strValue);

		// Don't process the following keys.
		if (Event.keyCode != 8 &&	// Backspace
			Event.keyCode != 9 &&	// Tab
			Event.keyCode != 37 &&	// Left Arrow
			Event.keyCode != 38 &&	// Up Arrow
			Event.keyCode != 39 &&	// Right Arrow
			Event.keyCode != 40 &&	// Down Arrow
			Event.keyCode != 46		// Delete
		) {
			// Don't allow entry if the user has already met the maximum number of characters.
			if (strValue.length == this.maxlength)
				Event.preventDefault();
		}
		else if (Event.keyCode == 38) {
			// Don't allow a value increase if the value is already at max.
			if (dblValue >= this.max)
				Event.preventDefault();
		}
		else if (Event.keyCode == 40) {
			// Don't allow a value decrease if the value is already at min.
			if (dblValue <= this.min)
				Event.preventDefault();
		}
	}

}